// Crest typography: selectable font families used for the family name, motto and year.
// The on-screen preview uses the web fonts loaded in index.html. When the crest is
// rasterized to PNG (downloads / keepsake PDF) the fonts are embedded into the SVG so
// they render identically inside the sandboxed <img> used for canvas drawing.

export const CREST_FONTS = [
  { id: "serif", label: "Serif", stack: "'Cinzel', Georgia, 'Times New Roman', serif", widthFactor: 0.62 },
  { id: "sans", label: "Sans-serif", stack: "'Montserrat', 'Helvetica Neue', Arial, sans-serif", widthFactor: 0.58 },
  { id: "script", label: "Script", stack: "'Great Vibes', 'Segoe Script', cursive", widthFactor: 0.74 },
  { id: "blackletter", label: "Medieval", stack: "'UnifrakturCook', 'Cormorant Garamond', Georgia, serif", widthFactor: 0.7 },
];

export function crestFont(id) {
  return CREST_FONTS.find((f) => f.id === id) || CREST_FONTS[0];
}
export function crestFontStack(id) {
  return crestFont(id).stack;
}

const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Montserrat:wght@600;700&family=Great+Vibes&family=UnifrakturCook:wght@700&display=swap";

let _cssCache = null;

// Fetch the Google Fonts CSS and inline every woff2 file as a base64 data URI so the
// resulting @font-face rules work inside a serialized (sandboxed) SVG.
export async function embeddedCrestFontCss() {
  if (_cssCache !== null) return _cssCache;
  try {
    const cssText = await (await fetch(GOOGLE_FONTS_URL)).text();
    const urls = [...new Set([...cssText.matchAll(/url\((https:\/\/[^)]+\.woff2)\)/g)].map((m) => m[1]))];
    const map = {};
    await Promise.all(
      urls.map(async (u) => {
        const buf = await (await fetch(u)).arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        map[u] = btoa(binary);
      })
    );
    _cssCache = cssText.replace(
      /url\((https:\/\/[^)]+\.woff2)\)/g,
      (m, u) => (map[u] ? `url(data:font/woff2;base64,${map[u]})` : m)
    );
  } catch (e) {
    _cssCache = ""; // graceful: fall back to the font stacks (Georgia/Arial etc.)
  }
  return _cssCache;
}

// Clone an <svg>, embed the crest fonts, and return the serialized markup.
export async function serializeSvgWithFonts(svg) {
  const clone = svg.cloneNode(true);
  const css = await embeddedCrestFontCss();
  if (css) {
    const styleEl = document.createElementNS("http://www.w3.org/2000/svg", "style");
    styleEl.textContent = css;
    clone.insertBefore(styleEl, clone.firstChild);
  }
  return new XMLSerializer().serializeToString(clone);
}
